import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

export interface DataTableColumn<T> {
  field: string;
  headerName: string;
  align?: 'left' | 'right' | 'center';
  render?: (row: T) => ReactNode;
  searchValue?: (row: T) => string | number | boolean | null | undefined;
  /** Shows a dropdown in the toolbar with every distinct value of this column, letting the user
   * narrow the table to one value — for bounded-domain fields (status, warehouse, category, ...),
   * not free text or per-row-unique values. */
  filterable?: boolean;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  loading?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  toolbarActions?: ReactNode;
  emptyMessage?: string;
  rowsPerPageOptions?: number[];
  defaultRowsPerPage?: number;
  getRowId?: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Shows a "Export CSV" button that downloads the currently filtered/searched rows. Default true. */
  exportable?: boolean;
  /** Base file name (without extension) for the CSV export. Defaults to "export". */
  exportFileName?: string;
}

function cellValue<T>(col: DataTableColumn<T>, row: T): string | number | boolean | null | undefined {
  if (col.searchValue) return col.searchValue(row);
  return (row as Record<string, unknown>)[col.field] as string | number | boolean | null | undefined;
}

function csvEscape(value: unknown): string {
  const str = value === null || value === undefined ? '' : String(value);
  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

function downloadCsv(fileName: string, columns: DataTableColumn<never>[], rows: never[]) {
  const exportColumns = columns.filter((c) => c.field !== 'action' && c.field !== 'actions');
  const lines = [
    exportColumns.map((c) => csvEscape(c.headerName)).join(','),
    ...rows.map((row) => exportColumns.map((c) => csvEscape(cellValue(c, row))).join(',')),
  ];
  const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName.toLowerCase().endsWith('.csv') ? fileName : `${fileName}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function DataTable<T extends { id: string }>({
  columns,
  rows,
  loading = false,
  searchable = true,
  searchPlaceholder,
  toolbarActions,
  emptyMessage,
  rowsPerPageOptions = [5, 10, 25],
  defaultRowsPerPage = 10,
  getRowId = (row) => row.id,
  onRowClick,
  exportable = true,
  exportFileName = 'export',
}: DataTableProps<T>) {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});

  const filterableColumns = columns.filter((c) => c.filterable);

  const filterOptionsByField = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const col of filterableColumns) {
      const values = new Set<string>();
      for (const row of rows) {
        const value = cellValue(col, row);
        if (value !== null && value !== undefined && value !== '') values.add(String(value));
      }
      map[col.field] = Array.from(values).sort((a, b) => a.localeCompare(b));
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, columns]);

  const activeFilterCount = Object.values(columnFilters).filter(Boolean).length;

  const filteredRows = useMemo(() => {
    let result = rows;

    const term = search.trim().toLowerCase();
    if (term) {
      result = result.filter((row) =>
        columns.some((col) => {
          const value = cellValue(col, row);
          return value !== null && value !== undefined && String(value).toLowerCase().includes(term);
        }),
      );
    }

    for (const col of filterableColumns) {
      const selected = columnFilters[col.field];
      if (!selected) continue;
      result = result.filter((row) => {
        const value = cellValue(col, row);
        return value !== null && value !== undefined && String(value) === selected;
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, columns, search, columnFilters]);

  const pagedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Paper variant="outlined">
      {(searchable || filterableColumns.length > 0 || toolbarActions || exportable) && (
        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          rowGap={1.5}
          columnGap={2}
          sx={{ p: { xs: 1.5, sm: 2 } }}
        >
          {/* Search + per-column filter dropdowns + Clear — one flex-wrap group so they read as a
              single toolbar row on desktop, wrapping onto their own lines only once the screen is
              too narrow to fit them, rather than search and filters always occupying separate rows
              regardless of available width. */}
          {/* Full width on a phone so the search box inside can actually resolve its own 100%
              against the toolbar rather than against its content width. */}
          <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            {searchable && (
              <TextField
                size="small"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                placeholder={searchPlaceholder ?? t('table.search')}
                // Full width on a phone — a 240px box floating in a 328px row wastes the space and
                // reads as misaligned; from sm up it goes back to its natural fixed width.
                sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 240 } }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
            {filterableColumns.map((col) => (
              <TextField
                key={col.field}
                select
                size="small"
                label={col.headerName}
                value={columnFilters[col.field] ?? ''}
                onChange={(e) => {
                  setColumnFilters((prev) => ({ ...prev, [col.field]: e.target.value }));
                  setPage(0);
                }}
                sx={{ minWidth: 160, flex: { xs: 1, sm: 'none' } }}
              >
                <MenuItem value="">{t('table.all')}</MenuItem>
                {(filterOptionsByField[col.field] ?? []).map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </TextField>
            ))}
            {activeFilterCount > 0 && (
              <Button size="small" onClick={() => setColumnFilters({})}>
                {t('table.clearFilters')}
              </Button>
            )}
          </Stack>

          <Stack direction="row" spacing={1}>
            {toolbarActions}
            {exportable && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
                onClick={() => downloadCsv(exportFileName, columns as DataTableColumn<never>[], filteredRows as never[])}
                disabled={filteredRows.length === 0}
              >
                {t('table.exportCsv')}
              </Button>
            )}
          </Stack>
        </Stack>
      )}

      {loading && <LinearProgress />}

      <TableContainer sx={{ px: { xs: 1, sm: 2 } }}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.field} align={col.align ?? 'left'}>
                  {col.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">{emptyMessage ?? t('table.noResults')}</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              pagedRows.map((row) => (
                <TableRow
                  key={getRowId(row)}
                  hover={Boolean(onRowClick)}
                  onClick={() => onRowClick?.(row)}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {columns.map((col) => (
                    <TableCell key={col.field} align={col.align ?? 'left'}>
                      {col.render ? col.render(row) : String((row as Record<string, unknown>)[col.field] ?? '')}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredRows.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number(e.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={rowsPerPageOptions}
        labelRowsPerPage={t('table.rowsPerPage')}
        // MUI lays the pagination bar out as one non-wrapping row; on a phone the label + selector
        // + range + arrows overflow it. Let it wrap and drop the built-in left padding so it stays
        // inside the card.
        sx={{
          '& .MuiTablePagination-toolbar': {
            flexWrap: 'wrap',
            rowGap: 1,
            pl: { xs: 1, sm: 2 },
          },
        }}
      />
    </Paper>
  );
}
